import React from 'react';
import { Award, Globe, Target, Briefcase } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const About = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.1 });
  const [badgesRef, badgesVisible] = useScrollAnimation({ threshold: 0.2 });
  const [featuresRef, featuresVisible] = useScrollAnimation({ threshold: 0.1 });

  const features = [
    {
      icon: Award,
      title: "Industry Excellence",
      description: "Decades of combined experience in property consultancy and management across diverse sectors."
    },
    {
      icon: Globe,
      title: "Global Perspective",
      description: "International best practices applied locally, ensuring your clients receive world-class guidance."
    },
    {
      icon: Target,
      title: "Strategic Approach",
      description: "Data-driven insights and market analysis to help agents make informed decisions."
    },
    {
      icon: Briefcase,
      title: "Professional Partnership",
      description: "We work alongside agents as trusted advisors, supporting your success every step of the way."
    }
  ];

  return (
    <section id="about" className="py-20 lg:py-28 bg-gradient-to-b from-white to-[hsl(210,15%,93%)]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div ref={titleRef} className={`text-center mb-16 scroll-animate ${titleVisible ? 'visible' : ''}`}>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[hsl(210,45%,45%)] mb-4">
              About PA Global
            </h2>
            <div className="w-24 h-1 bg-[hsl(210,45%,45%)] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Management & Consultancy
            </p>
          </div>

          {/* Main Content */}
          <div ref={contentRef} className={`mb-16 max-w-4xl mx-auto scroll-animate ${contentVisible ? 'visible' : ''}`}>
            <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-6 text-center sm:whitespace-nowrap">
              Your Trusted Property Consultancy Partner
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed text-center">
              PA Global Management & Consultancy is a premier property consultancy firm dedicated to empowering real estate agents with expert guidance and strategic insights. We specialize in private sector properties, social housing developments, and residential property consultation.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed text-center">
              Our team of seasoned consultants brings extensive industry knowledge and a commitment to excellence. We understand the challenges agents face in today's dynamic property market and provide tailored solutions that drive success.
            </p>
            <p className="text-gray-700 leading-relaxed text-center">
              Whether you're navigating complex transactions, seeking market intelligence, or need expert advice on property valuations, PA Global is your dedicated partner for professional growth and client satisfaction.
            </p>
            
            {/* Experience & Consultation Badges */}
            <div ref={badgesRef} className={`flex flex-col sm:flex-row justify-center gap-6 mt-8 scroll-animate-scale ${badgesVisible ? 'visible' : ''}`}>
              <div className="bg-[hsl(210,45%,45%)] text-white px-8 py-6 rounded-lg shadow-xl">
                <p className="text-4xl font-bold text-center uppercase">25+</p>
                <p className="text-sm text-center uppercase">Years Combined Experience</p>
              </div>
              <div className="bg-[hsl(215,65%,22%)] text-white px-8 py-6 rounded-lg shadow-xl flex items-center justify-center">
                <p className="text-base font-bold text-center uppercase leading-tight">Expert Property Consultancy and Management</p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div ref={featuresRef} className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-8 scroll-animate-stagger ${featuresVisible ? 'visible' : ''}`}>
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 border-t-4 border-[hsl(210,45%,45%)]"
              >
                <div className="bg-[hsl(215,65%,22%)] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="text-white" size={28} />
                </div>
                <h4 className="text-xl font-bold text-[#1a3a5c] mb-3">{feature.title}</h4>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
